import type { KnowledgeCatalog, KnowledgeNode, KnowledgeGraph } from "../knowledge/KnowledgeGraph";
import type { RecipeRegistry } from "../crafting/RecipeRegistry";

type Tab = "knowledge" | "skills";

function parseMarkdown(md: string): string {
  let html = md
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/\*\*\*(.+?)\*\*\*/g, "<em><strong>$1</strong></em>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  const lines = html.split("\n");
  const out: string[] = [];
  let inList: false | "ol" | "ul" = false;

  for (const line of lines) {
    if (/^(\d+)\. /.test(line) || /^[-*] /.test(line)) {
      if (!inList) {
        const tag = /^(\d+)\. /.test(line) ? "ol" : "ul";
        out.push(`<${tag}>`);
        inList = tag;
      }
      const item = line.replace(/^(\d+)\. /, "").replace(/^[-*] /, "");
      out.push(`<li>${item}</li>`);
    } else {
      if (inList) {
        out.push(`</${inList}>`);
        inList = false;
      }
      if (line.startsWith("<h") || line.startsWith("<")) {
        out.push(line);
      } else if (line.trim() === "") {
        out.push("<br>");
      } else {
        out.push(`<p>${line}</p>`);
      }
    }
  }
  if (inList) out.push(`</${inList}>`);
  return out.join("\n");
}

const STYLE = `
  #knowledge-overlay { position: fixed; inset: 0; background: #000b; display: none; z-index: 100; justify-content: center; align-items: center; font: 15px system-ui, sans-serif; color: #e8e0f0; }
  #knowledge-overlay.show { display: flex; }
  #knowledge-card { background: #1b1626; border: 1px solid #6b5ca5; border-radius: 12px; padding: 24px 28px; max-width: 820px; width: 95%; max-height: 85vh; display: flex; flex-direction: column; }
  #knowledge-card h2 { margin: 0 0 12px; font-size: 20px; color: #c4b5e0; }

  .kb-tabs { display: flex; gap: 4px; margin-bottom: 16px; }
  .kb-tab { background: #2b2140; color: #9a88c0; border: 1px solid #6b5ca5; border-radius: 8px 8px 0 0; padding: 8px 20px; font-size: 14px; cursor: pointer; border-bottom: none; transition: background .15s, color .15s; }
  .kb-tab.active { background: #43356b; color: #e8e0f0; }
  .kb-tab:hover:not(.active) { background: #362a50; }

  .kb-tab-content { flex: 1; overflow-y: auto; min-height: 0; }
  .kb-tab-panel { display: none; }
  .kb-tab-panel.active { display: block; }

  .tree-container { position: relative; overflow: auto; padding: 12px 0; }
  .tree-svg { position: absolute; top: 0; left: 0; pointer-events: none; }
  .tree-columns { display: flex; gap: 0; position: relative; min-width: max-content; }

  .tree-col { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 0 18px; min-width: 120px; }
  .tree-col-label { font-size: 11px; color: #7a6ea0; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 4px; white-space: nowrap; }

  .tree-node { position: relative; display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 10px; cursor: pointer; transition: background .15s, box-shadow .15s; border: 1px solid transparent; min-width: 140px; }
  .tree-node:hover { background: #2b2140; }
  .tree-node.selected { background: #2b2140; border-color: #6b5ca5; box-shadow: 0 0 12px #6b5ca544; }
  .tree-node.understood .node-dot { background: #5cdb7f; box-shadow: 0 0 6px #5cdb7f66; }
  .tree-node.partiallyUnderstood .node-dot { background: #f0c24a; box-shadow: 0 0 6px #f0c24a66; }
  .tree-node.discovered .node-dot { background: #5ca5d6; box-shadow: 0 0 6px #5ca5d666; }
  .tree-node.locked .node-dot { background: #4a4458; }
  .tree-node.locked .node-title { color: #6b6080; }
  .tree-node.locked .node-summary { color: #5a5070; }
  .node-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .node-text { min-width: 0; }
  .node-title { font-size: 13px; font-weight: 600; color: #e8e0f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 110px; }
  .node-summary { font-size: 11px; color: #b8acd9; margin-top: 2px; line-height: 1.3; display: none; }


  .node-detail { background: #2b2140; border: 1px solid #6b5ca5; border-radius: 8px; padding: 14px 16px; margin-top: 12px; font-size: 14px; line-height: 1.5; }
  .node-detail h4 { margin: 0 0 6px; color: #c4b5e0; font-size: 15px; }
  .node-detail p { margin: 0; color: #b8acd9; }
  .node-detail .detail-meta { margin-top: 8px; font-size: 12px; color: #7a6ea0; }

  .legend { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 14px; padding-top: 10px; border-top: 1px solid #362a50; }
  .legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #9a88c0; }
  .legend-dot { width: 8px; height: 8px; border-radius: 50%; }

  .skills-list { display: flex; flex-direction: column; gap: 8px; }
  .skill-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: #2b2140; border-radius: 8px; border: 1px solid #362a50; }
  .skill-item .skill-icon { font-size: 20px; }
  .skill-item .skill-info { flex: 1; }
  .skill-item .skill-name { font-size: 14px; font-weight: 600; color: #e8e0f0; }
  .skill-item .skill-desc { font-size: 12px; color: #b8acd9; margin-top: 2px; }
  .skills-empty { color: #7a6ea0; font-style: italic; text-align: center; padding: 24px; }

  #knowledge-card .close { display: block; margin: 14px auto 0; background: #43356b; color: #fff; border: 1px solid #6b5ca5; border-radius: 6px; padding: 8px 24px; font-size: 15px; cursor: pointer; }
  #knowledge-card .close:hover { background: #6b5ca5; }

  .node-detail .more-btn { display: inline-block; margin-top: 10px; background: #43356b; color: #c4b5e0; border: 1px solid #6b5ca5; border-radius: 6px; padding: 6px 16px; font-size: 13px; cursor: pointer; transition: background .15s; }
  .node-detail .more-btn:hover { background: #6b5ca5; color: #fff; }

  #article-overlay { position: fixed; inset: 0; background: #000b; display: none; z-index: 200; justify-content: center; align-items: center; font: 15px system-ui, sans-serif; color: #e8e0f0; }
  #article-overlay.show { display: flex; }
  #article-card { background: #1b1626; border: 1px solid #6b5ca5; border-radius: 12px; padding: 28px 36px; max-width: 680px; width: 92%; max-height: 80vh; overflow-y: auto; }
  #article-card h1 { margin: 0 0 12px; font-size: 22px; color: #c4b5e0; }
  #article-card h2 { margin: 18px 0 8px; font-size: 17px; color: #b8acd9; border-bottom: 1px solid #362a50; padding-bottom: 4px; }
  #article-card h3 { margin: 14px 0 6px; font-size: 15px; color: #a898c8; }
  #article-card p { margin: 8px 0; line-height: 1.6; color: #d0c8e0; }
  #article-card strong { color: #e8e0f0; }
  #article-card em { color: #c4b5e0; }
  #article-card ul, #article-card ol { margin: 8px 0 8px 20px; }
  #article-card li { margin: 4px 0; line-height: 1.5; color: #d0c8e0; }
  #article-card code { background: #2b2140; padding: 2px 6px; border-radius: 4px; font-size: 13px; color: #f0c24a; }
  #article-card a { color: #5ca5d6; text-decoration: none; }
  #article-card a:hover { text-decoration: underline; }
  #article-card .close-article { display: block; margin: 18px auto 0; background: #43356b; color: #fff; border: 1px solid #6b5ca5; border-radius: 6px; padding: 8px 24px; font-size: 15px; cursor: pointer; }
  #article-card .close-article:hover { background: #6b5ca5; }
  #article-card .article-loading { color: #7a6ea0; font-style: italic; text-align: center; padding: 24px; }
  #article-card .article-not-found { color: #7a6ea0; font-style: italic; text-align: center; padding: 24px; }

  #knowledge-overlay, #article-overlay { background: #1d352966; color: #314338; }
  #knowledge-card, #article-card { background: #fffdf5; border: 2px solid #b9d8aa; border-radius: 20px; box-shadow: 0 12px 32px #1d352966; }
  #knowledge-card h2, #article-card h1 { color: #31583b; }
  #article-card h2, #article-card h3, #article-card em { color: #42694b; border-color: #b9d8aa; }
  .kb-tab, .node-detail, .skill-item, #article-card code { background: #eef7e9; border-color: #b9d8aa; color: #314338; }
  .kb-tab.active, .kb-tab:hover:not(.active), .tree-node:hover, .tree-node.selected { background: #dff2d8; color: #173420; border-color: #79b889; }
  .tree-col-label, .legend-item, .node-summary, .node-detail .detail-meta, .skill-item .skill-desc, .skills-empty, #article-card .article-loading, #article-card .article-not-found { color: #42694b; }
  .node-title, .node-detail h4, .node-detail p, .skill-item .skill-name, #article-card p, #article-card strong, #article-card li { color: #314338; }
  .legend { border-color: #b9d8aa; }
  #knowledge-card .close, #article-card .close-article, .node-detail .more-btn { background: #79b889; color: #173420; border: 2px solid #dff2d8; border-radius: 999px; box-shadow: 0 3px 0 #42694b; font-weight: 600; }
  #knowledge-card .close:hover, #article-card .close-article:hover, .node-detail .more-btn:hover { background: #a7d198; color: #173420; }
`;

const RECIPE_ICONS: Record<string, string> = {
  "recipe.spyglass": "🔭",
  "recipe.glowing-stone": "✨",
};

export interface KnowledgeOverlay {
  refresh(): void;
  toggle(): void;
  close(): void;
  readonly element: HTMLElement;
}

export function createKnowledgeOverlay(
  knowledgeCatalog: KnowledgeCatalog,
  knowledge: KnowledgeGraph,
  recipes: RecipeRegistry | undefined,
): KnowledgeOverlay {
  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  const el = document.createElement("div");
  el.id = "knowledge-overlay";
  el.innerHTML = `
    <div id="knowledge-card">
      <h2>Knowledge</h2>
      <div class="kb-tabs">
        <button class="kb-tab active" data-tab="knowledge">Knowledge</button>
        <button class="kb-tab" data-tab="skills">Skills</button>
      </div>
      <div class="kb-tab-content">
        <div class="kb-tab-panel active" id="tab-knowledge">
          <div class="tree-container" id="tree-container"></div>
          <div id="node-detail-container"></div>
          <div class="legend">
            <div class="legend-item"><div class="legend-dot" style="background:#5cdb7f"></div>Understood</div>
            <div class="legend-item"><div class="legend-dot" style="background:#f0c24a"></div>In Progress</div>
            <div class="legend-item"><div class="legend-dot" style="background:#5ca5d6"></div>Discovered</div>
            <div class="legend-item"><div class="legend-dot" style="background:#4a4458"></div>Locked</div>
          </div>
        </div>
        <div class="kb-tab-panel" id="tab-skills">
          <div id="skills-content"></div>
        </div>
      </div>
      <button class="close">Close</button>
    </div>
  `;
  document.body.appendChild(el);

  const articleOverlay = document.createElement("div");
  articleOverlay.id = "article-overlay";
  articleOverlay.innerHTML = `
    <div id="article-card">
      <div id="article-content"><div class="article-loading">Loading...</div></div>
      <button class="close-article">Close</button>
    </div>
  `;
  document.body.appendChild(articleOverlay);

  articleOverlay.addEventListener("click", (e) => {
    if (e.target === articleOverlay) closeArticle();
  });
  articleOverlay.querySelector(".close-article")!.addEventListener("click", closeArticle);

  function closeArticle(): void {
    articleOverlay.classList.remove("show");
  }

  async function showArticle(nodeId: string): Promise<void> {
    const contentEl = articleOverlay.querySelector<HTMLElement>("#article-content");
    if (!contentEl) return;
    contentEl.innerHTML = '<div class="article-loading">Loading...</div>';
    articleOverlay.classList.add("show");

    try {
      const resp = await fetch(`/content/knowledge/${nodeId}.md`);
      if (!resp.ok) {
        contentEl.innerHTML = '<div class="article-not-found">No article available for this topic yet.</div>';
        return;
      }
      const md = await resp.text();
      const node = knowledgeCatalog.get(nodeId);
      const title = node?.title ?? nodeId;
      contentEl.innerHTML = `<h1>${title}</h1>` + parseMarkdown(md);
    } catch {
      contentEl.innerHTML = '<div class="article-not-found">Could not load article.</div>';
    }
  }

  el.addEventListener("click", (e) => {
    if (e.target === el) el.classList.remove("show");
  });
  el.querySelector(".close")!.addEventListener("click", () => el.classList.remove("show"));

  const tabs = el.querySelectorAll<HTMLElement>(".kb-tab");
  const panels = el.querySelectorAll<HTMLElement>(".kb-tab-panel");
  let activeTab: Tab = "knowledge";

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      activeTab = (tab.dataset.tab as Tab) ?? "knowledge";
      tabs.forEach((t) => t.classList.toggle("active", t.dataset.tab === activeTab));
      panels.forEach((p) => p.classList.toggle("active", p.id === `tab-${activeTab}`));
    });
  });

  let selectedNodeId: string | undefined;

  function refresh(): void {
    renderKnowledgeTree();
    renderSkills();
  }

  function renderKnowledgeTree(): void {
    const container = el.querySelector<HTMLElement>("#tree-container");
    const detailContainer = el.querySelector<HTMLElement>("#node-detail-container");
    if (!container || !detailContainer) return;
    container.innerHTML = "";
    detailContainer.innerHTML = "";
    selectedNodeId = undefined;

    const allNodes = knowledgeCatalog.all();
    const progress = knowledge.getProgress();

    const ageColumns = new Map<number, KnowledgeNode[]>();
    for (const node of allNodes) {
      const age = node.ageLevel ?? 0;
      const list = ageColumns.get(age) ?? [];
      list.push(node);
      ageColumns.set(age, list);
    }

    const sortedAges = [...ageColumns.keys()].sort((a, b) => a - b);
    const NODE_W = 140;
    const NODE_H = 50;
    const COL_GAP = 40;
    const ROW_GAP = 18;
    const SVG_PAD = 20;

    const nodePositions = new Map<string, { x: number; y: number }>();

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.classList.add("tree-svg");

    const columnsWrap = document.createElement("div");
    columnsWrap.className = "tree-columns";

    let colX = 0;

    for (const age of sortedAges) {
      const nodes = ageColumns.get(age)!;
      const col = document.createElement("div");
      col.className = "tree-col";

      const label = document.createElement("div");
      label.className = "tree-col-label";
      label.textContent = age > 0 ? `Age ${age}` : "Starter";
      col.appendChild(label);

      let nodeY = 0;
      for (const node of nodes) {
        const state = progress[node.id] ?? "locked";
        const treeNode = document.createElement("div");
        treeNode.className = `tree-node ${state}`;
        treeNode.dataset.nodeId = node.id;

        const dot = document.createElement("div");
        dot.className = "node-dot";
        const text = document.createElement("div");
        text.className = "node-text";
        const titleEl = document.createElement("div");
        titleEl.className = "node-title";
        titleEl.textContent = node.title;
        titleEl.title = node.title;
        const summary = document.createElement("div");
        summary.className = "node-summary";
        summary.textContent = node.summary;
        text.append(titleEl, summary);
        treeNode.append(dot, text);

        nodePositions.set(node.id, { x: colX + SVG_PAD, y: nodeY + SVG_PAD + 20 });
        treeNode.style.width = `${NODE_W}px`;

        treeNode.addEventListener("click", () => selectNode(node.id));
        col.appendChild(treeNode);
        nodeY += NODE_H + ROW_GAP;
      }

      columnsWrap.appendChild(col);
      colX += NODE_W + COL_GAP;
    }

    container.appendChild(columnsWrap);

    const totalW = colX - COL_GAP + SVG_PAD * 2;
    const maxColNodes = Math.max(...sortedAges.map((a) => ageColumns.get(a)!.length));
    const totalH = maxColNodes * (NODE_H + ROW_GAP) + SVG_PAD * 2 + 24;
    svg.setAttribute("width", String(totalW));
    svg.setAttribute("height", String(totalH));
    svg.style.width = `${totalW}px`;
    svg.style.height = `${totalH}px`;

    const drawnEdges = new Set<string>();
    for (const node of allNodes) {
      const from = nodePositions.get(node.id);
      if (!from) continue;
      for (const linkId of node.links) {
        const edgeKey = [node.id, linkId].sort().join("::");
        if (drawnEdges.has(edgeKey)) continue;
        drawnEdges.add(edgeKey);
        const to = nodePositions.get(linkId);
        if (!to) continue;

        const fromState = progress[node.id] ?? "locked";
        const toState = progress[linkId] ?? "locked";
        const bothKnown = fromState !== "locked" && toState !== "locked";

        const path = document.createElementNS(svgNS, "path");
        const dx = to.x - from.x;
        const midX = from.x + dx * 0.5;
        path.setAttribute("d", `M${from.x},${from.y} C${midX},${from.y} ${midX},${to.y} ${to.x},${to.y}`);
        path.setAttribute("fill", "none");
        path.setAttribute("stroke", bothKnown ? "#6b5ca5" : "#362a50");
        path.setAttribute("stroke-width", bothKnown ? "2" : "1");
        path.setAttribute("stroke-dasharray", bothKnown ? "none" : "4,4");
        path.setAttribute("opacity", bothKnown ? "0.8" : "0.4");
        svg.appendChild(path);
      }
    }

    container.insertBefore(svg, columnsWrap);
  }

  function selectNode(nodeId: string): void {
    const prev = el.querySelector(".tree-node.selected");
    if (prev) prev.classList.remove("selected");

    if (selectedNodeId === nodeId) {
      selectedNodeId = undefined;
      const detailContainer = el.querySelector<HTMLElement>("#node-detail-container");
      if (detailContainer) detailContainer.innerHTML = "";
      return;
    }
    selectedNodeId = nodeId;

    const treeNode = el.querySelector(`.tree-node[data-node-id="${nodeId}"]`);
    if (treeNode) treeNode.classList.add("selected");

    const node = knowledgeCatalog.get(nodeId);
    const detailContainer = el.querySelector<HTMLElement>("#node-detail-container");
    if (!node || !detailContainer) return;

    const progress = knowledge.getProgress();
    const state = progress[nodeId] ?? "locked";
    const stateLabel = state === "understood" ? "Understood"
      : state === "partiallyUnderstood" ? "In Progress"
      : state === "discovered" ? "Discovered"
      : "Locked";

    const linksHtml = node.links.length > 0
      ? `<div class="detail-meta">Connected to: ${node.links.map((lid) => knowledgeCatalog.get(lid)?.title ?? lid).join(", ")}</div>`
      : "";

    detailContainer.innerHTML = `
      <div class="node-detail">
        <h4>${node.title}</h4>
        <p>${node.summary}</p>
        <div class="detail-meta">Status: ${stateLabel}${node.ageLevel ? ` · Age ${node.ageLevel}` : ""}</div>
        ${linksHtml}
        <button class="more-btn" data-article-id="${nodeId}">Read more...</button>
      </div>
    `;

    detailContainer.querySelector<HTMLButtonElement>(".more-btn")?.addEventListener("click", () => {
      void showArticle(nodeId);
    });
  }

  function renderSkills(): void {
    const content = el.querySelector<HTMLElement>("#skills-content");
    if (!content) return;
    content.innerHTML = "";

    if (!recipes) {
      content.innerHTML = '<div class="skills-empty">No skills discovered yet.</div>';
      return;
    }

    const discovered = recipes.getDiscovered(knowledge);
    if (discovered.length === 0) {
      content.innerHTML = '<div class="skills-empty">Discover artifacts and learn knowledge to unlock skills!</div>';
      return;
    }

    const list = document.createElement("div");
    list.className = "skills-list";
    for (const recipe of discovered) {
      const item = document.createElement("div");
      item.className = "skill-item";
      const icon = document.createElement("div");
      icon.className = "skill-icon";
      icon.textContent = RECIPE_ICONS[recipe.id] ?? "🔧";
      const info = document.createElement("div");
      info.className = "skill-info";
      const name = document.createElement("div");
      name.className = "skill-name";
      name.textContent = recipe.produces.replace(/^[^.]+\./, "").replace(/-/g, " ");
      const desc = document.createElement("div");
      desc.className = "skill-desc";
      desc.textContent = recipe.message;
      info.append(name, desc);
      item.append(icon, info);
      list.appendChild(item);
    }
    content.appendChild(list);
  }

  return {
    refresh,
    toggle() { el.classList.toggle("show"); },
    close() { el.classList.remove("show"); },
    get element() { return el; },
  };
}
